import { Box, Typography, Grid, Chip, Avatar, Stack } from '@mui/material';
import { Person, Business, AccessTime, LocationOn, Phone } from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Badge } from '../../../../shared/components/ui/Badge';
import { Card } from '../../../../shared/components/ui/Card';
import type { Visitor } from '../../../../shared/services/visitors/visitorService';

interface VisitorInfoProps {
  visitor: Visitor;
  actions?: React.ReactNode;
}

const formatDate = (dateString: string): string => {
  try {
    return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return 'Data inválida';
  }
};

export function VisitorInfo({ visitor, actions }: VisitorInfoProps) {
  return (
    <>
      {/* Topo: Avatar, info e ações lado a lado */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Avatar sx={{ width: 110, height: 110, fontSize: 48, bgcolor: 'primary.main' }}>
            {visitor.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography sx={{ color: '#23272E', fontWeight: 800, fontSize: 28, letterSpacing: -1 }}>
              {visitor.name} <Badge color={visitor.status_flag === 'ACTIVE' ? 'success' : 'default'}>{visitor.status_flag === 'ACTIVE' ? 'Ativo' : 'Inativo'}</Badge>
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: 16, fontWeight: 500 }}>
              Visitante
            </Typography>
            <Stack direction="row" spacing={2} mt={1} alignItems="center">
              {visitor.phone_number && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Phone sx={{ color: '#6B7280', fontSize: 16 }} />
                  <Typography sx={{ color: '#6B7280', fontSize: 14 }}>{visitor.phone_number}</Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Box>
        {actions}
      </Box>

      {/* Detalhes */}
      <Grid container spacing={3}>
        {/* Informações Pessoais */}
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Person sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color="#23272E">Dados do Visitante</Typography>
            </Box>
            <Box component="dl" sx={{ m: 0 }}>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Nome:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{visitor.name}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>CPF:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{visitor.document_number}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Telefone:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{visitor.phone_number || '-'}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Status:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>
                  <Badge color={visitor.status_flag === 'ACTIVE' ? 'success' : 'default'}>{visitor.status_flag === 'ACTIVE' ? 'Ativo' : 'Inativo'}</Badge>
                </dd>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Informações de Acesso */}
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Business sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color="#23272E">Empresa & Responsável</Typography>
            </Box>
            <Box component="dl" sx={{ m: 0 }}>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Empresa:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{visitor.tenant?.name || '-'}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Responsável:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{visitor.user?.name || '-'}</dd>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Período de Acesso */}
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccessTime sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color="#23272E">Período de Acesso</Typography>
            </Box>
            <Box component="dl" sx={{ m: 0 }}>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Início:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{formatDate(visitor.start_date)}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Fim:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{formatDate(visitor.end_date)}</dd>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Áreas de Acesso */}
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOn sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color="#23272E">Áreas de Acesso</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {visitor.areas && visitor.areas.length > 0 ? (
                visitor.areas.map((area) => (
                  <Chip
                    key={area.id}
                    label={area.name}
                    variant="outlined"
                    color="primary"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma área de acesso definida
                </Typography>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Imagens */}
        {visitor.images && visitor.images.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3 }}>
              <Typography fontWeight={700} fontSize={17} color="#23272E" sx={{ mb: 2 }}>
                Imagens
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {visitor.images.map((image, index) => (
                  <Box
                    key={index}
                    component="img"
                    src={image}
                    alt={`Imagem ${index + 1} do visitante`}
                    sx={{
                      width: 120,
                      height: 120,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid #e0e0e0'
                    }}
                  />
                ))}
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>
    </>
  );
} 